'use client';

import { forwardRef, useEffect, useRef, PropsWithChildren, FC, useState } from 'react';
import FocusLock from 'react-focus-lock';

import { useTranslations } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';

import Button from '../button/button';
import IconX from '../icon/x';
import { useMediaQuery } from 'react-responsive';

interface ModalProps {
	close?: () => void;
	ModalHeader?: FC<{ close?: () => void }>;
	ModalFooter?: FC<{ close?: () => void }>;
	contentContainerClassNames?: string;
	headerClassNames?: string;
	focusContainerClassNames?: string;
	overlayClassName?: string;
	mainContainerClassName?: string;
	isMobile?: boolean;
	mobileFullScreen?: boolean;
	mobileAnimate?: boolean;
	isAnimating?: boolean;
	setIsAnimating?: (isAnimating: boolean) => void;
	modalPaddingClassNames?: string;
	useNavBreakpoint?: boolean;
}

function ModalOverlay({ className }: { className?: string }) {
	return <div className={cn('bg-backdrop absolute top-0 left-0 right-0 bottom-0', className)} />;
}

const ModalContent = forwardRef(
	(
		{
			children,
			close,
			ModalHeader,
			ModalFooter,
			contentContainerClassNames,
			headerClassNames,
			focusContainerClassNames,
			isMobile,
			mobileFullScreen,
			mobileAnimate,
			isAnimating,
			setIsAnimating,
			modalPaddingClassNames = 'spacing-modal-padding-x',
			useNavBreakpoint = false,
		}: PropsWithChildren<ModalProps>,
		ref: React.Ref<HTMLDivElement>
	) => {
		const t = useTranslations();
		const containerRef = useRef<HTMLDivElement>(null);
		useEffect(() => {
			let originalOverflows: Map<Element, string> | null = null;
			let originalPadding = '0px';
			let scrollY = 0;
			let scrollX = 0;

			if (isMobile && mobileAnimate && isAnimating) {
				setIsAnimating?.(false);

				// Save current scroll position
				scrollY = window.scrollY;
				scrollX = window.scrollX;

				// Save original padding
				originalPadding = window.getComputedStyle(document.body).paddingRight;
				const originalPaddingValue = parseFloat(originalPadding) || 0;

				// Calculate scrollbar width before hiding it
				const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

				// Add padding to compensate for scrollbar removal (sum to existing padding)
				if (scrollbarWidth > 0) {
					document.body.style.paddingRight = `${originalPaddingValue + scrollbarWidth}px`;
				}

				// iOS-specific fix: use position fixed to prevent scroll
				document.body.style.position = 'fixed';
				document.body.style.top = `-${scrollY}px`;
				document.body.style.left = `-${scrollX}px`;
				document.body.style.width = '100%';

				// Lock body and html scroll with iOS-specific properties
				document.body.style.overflow = 'hidden';
				document.body.style.touchAction = 'none';
				document.body.style.overscrollBehavior = 'none';
				document.documentElement.style.overflow = 'hidden';

				// Store original overflow values to restore later
				originalOverflows = new Map();

				// Lock background elements but exclude our modal and its elements
				const allElements = document.querySelectorAll('*');
				Array.from(allElements).forEach(element => {
					const computedStyle = window.getComputedStyle(element);
					if (
						(computedStyle.overflow === 'auto' || computedStyle.overflow === 'scroll') &&
						element !== containerRef.current &&
						!containerRef.current?.contains(element)
					) {
						originalOverflows?.set(element, (element as HTMLElement).style.overflow);
						(element as HTMLElement).style.overflow = 'hidden';
					}
				});
			}
			return () => {
				// Restore body styles
				document.body.style.position = '';
				document.body.style.top = '';
				document.body.style.left = '';
				document.body.style.width = '';
				document.body.style.overflow = 'unset';
				document.body.style.touchAction = '';
				document.body.style.overscrollBehavior = '';
				document.documentElement.style.overflow = 'unset';
				document.body.style.paddingRight = originalPadding;

				// Restore scroll position
				if (scrollY > 0 || scrollX > 0) {
					window.scrollTo(scrollX, scrollY);
				}

				// Restore original overflow values
				if (originalOverflows) {
					originalOverflows.forEach((originalOverflow, element) => {
						(element as HTMLElement).style.overflow = originalOverflow;
					});
				}
			};
		}, []);

		return (
			<FocusLock
				returnFocus
				className={cn(
					['bg-white', 'relative', 'z-1000', 'w-full', 'h-full', 'max-h-[95vh]', 'overflow-hidden'],
					{
						'max-w-[690px]': !useNavBreakpoint,
						'max-w-[1100px]': useNavBreakpoint,
						'md:h-[540px]': !useNavBreakpoint,
						'header:h-[540px]': useNavBreakpoint,
						'max-h-full': isMobile && mobileFullScreen,
						'rounded-t-[15px]': isMobile && !mobileFullScreen,
						'h-auto': isMobile && !mobileFullScreen,
						fixed: isMobile && mobileAnimate,
						'bottom-0': isMobile && mobileAnimate,
						'translate-y-full': isMobile && mobileAnimate,
						'transition-transform': isMobile && mobileAnimate,
						'duration-500': isMobile && mobileAnimate,
						'ease-in-out': isMobile && mobileAnimate,
						'translate-y-0': isMobile && mobileAnimate && !isAnimating,
					},
					focusContainerClassNames
				)}
				ref={ref}
				lockProps={{ id: 'modal' }}
			>
				<div role="dialog" ref={containerRef} className={cn(['flex', 'flex-col', 'h-full'])}>
					<div className="ModalHeaderWrapper">
						{ModalHeader ? (
							<ModalHeader close={close} />
						) : (
							<div
								className={cn(
									'h-[24px] min-h-[24px] flex flex-row justify-end items-center z-[60] spacing-modal-padding-x mt-300 mb-200',
									{
										'mt-250': !mobileFullScreen,
										'md:mt-300': !mobileFullScreen && !useNavBreakpoint,
										'header:mt-300': !mobileFullScreen && useNavBreakpoint,
									},
									headerClassNames
								)}
							>
								{close && (
									<Button intent="unstyled" onClick={close} className="w-[24px] h-[24px]">
										<IconX width={24} height={24} />
										<span className="visually-hidden">{t('close')}</span>
									</Button>
								)}
							</div>
						)}
					</div>
					<div className={cn('w-full h-full overflow-auto', modalPaddingClassNames)}>
						<div className={cn('h-full flex flex-col justify-center', contentContainerClassNames)}>
							{children}
						</div>
					</div>
					<div className="ModalFooterWrapper">{ModalFooter && <ModalFooter close={close} />}</div>
				</div>
			</FocusLock>
		);
	}
);

export default function Modal({
	close,
	children,
	ModalHeader,
	ModalFooter,
	contentContainerClassNames,
	focusContainerClassNames,
	overlayClassName,
	mainContainerClassName,
	mobileFullScreen = true,
	mobileAnimate = false,
	modalPaddingClassNames,
	headerClassNames,
	isMobile: isMobileProp,
	useNavBreakpoint = false,
}: PropsWithChildren<ModalProps>) {
	const containerRef = useRef<HTMLDivElement>(null);
	const modalRef = useRef<HTMLDivElement>(null);
	const isMobileFromQuery = useMediaQuery({ maxWidth: useNavBreakpoint ? 1100 : 690 });
	const isMobile = isMobileProp ?? isMobileFromQuery;
	const [isAnimating, setIsAnimating] = useState(true);

	const onClose = () => {
		if (isMobile && mobileAnimate) {
			setIsAnimating(true);
			setTimeout(() => {
				setIsAnimating(false);
				close?.();
			}, 500);
		} else {
			close?.();
		}
	};

	useEffect(() => {
		const containerEl = containerRef.current;
		const modalEl = modalRef.current;

		const onOutsideClick = (evt: MouseEvent) => {
			// The clicked element is outside the modal and still in the DOM, and within the modal container
			if (
				modalEl &&
				containerEl &&
				document.contains(evt.target as Node) &&
				!modalEl.contains(evt.target as Node) &&
				containerEl.contains(evt.target as Node) &&
				close
			) {
				onClose();
			}
		};

		const onKeyDown = (evt: KeyboardEvent) => {
			if (close && evt.key === 'Escape') {
				onClose();
			}
		};

		// Save current scroll position
		const scrollY = window.scrollY;
		const scrollX = window.scrollX;

		// Save original padding
		const originalPadding = window.getComputedStyle(document.body).paddingRight;
		const originalPaddingValue = parseFloat(originalPadding) || 0;

		// Calculate scrollbar width before hiding it
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

		// Add padding to compensate for scrollbar removal (sum to existing padding)
		if (scrollbarWidth > 0) {
			document.body.style.paddingRight = `${originalPaddingValue + scrollbarWidth}px`;
		}

		// iOS-specific fix: use position fixed to prevent scroll
		document.body.style.position = 'fixed';
		document.body.style.top = `-${scrollY}px`;
		document.body.style.left = `-${scrollX}px`;
		document.body.style.width = '100%';

		// Additional properties to prevent iOS scroll issues
		document.body.classList.add('overflow-hidden');
		document.body.style.overflow = 'hidden';
		document.body.style.touchAction = 'none';
		document.body.style.overscrollBehavior = 'none';

		document.addEventListener('click', onOutsideClick);
		document.addEventListener('keydown', onKeyDown);

		if (isMobile && !mobileFullScreen && mobileAnimate && modalEl) {
			if (modalEl.clientHeight >= window.innerHeight * 0.95) {
				modalEl.style.height = '100%';
			}
		}

		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('click', onOutsideClick);
			document.body.classList.remove('overflow-hidden');

			// Restore body styles
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.left = '';
			document.body.style.width = '';
			document.body.style.overflow = '';
			document.body.style.touchAction = '';
			document.body.style.overscrollBehavior = '';
			document.body.style.paddingRight = originalPadding;

			// Restore scroll position
			window.scrollTo(scrollX, scrollY);
		};
	}, [close]);

	return (
		<div
			className={cn(
				[
					'fixed',
					'top-0',
					'left-0',
					'right-0',
					'bottom-0',
					'z-1000',
					'overflow-auto',
					'flex',
					'flex-col',
					'items-center',
					'justify-center',
				],
				{
					'md:p-150': !useNavBreakpoint,
					'header:p-150': useNavBreakpoint,
				},
				mainContainerClassName,
				{
					'justify-end': isMobile && !mobileFullScreen,
				}
			)}
			ref={containerRef}
		>
			<ModalContent
				contentContainerClassNames={contentContainerClassNames}
				close={close && onClose}
				ref={modalRef}
				ModalHeader={ModalHeader}
				ModalFooter={ModalFooter}
				focusContainerClassNames={focusContainerClassNames}
				isMobile={isMobile}
				mobileFullScreen={mobileFullScreen}
				mobileAnimate={mobileAnimate}
				isAnimating={isAnimating}
				setIsAnimating={setIsAnimating}
				modalPaddingClassNames={modalPaddingClassNames}
				headerClassNames={headerClassNames}
				useNavBreakpoint={useNavBreakpoint}
			>
				{children}
			</ModalContent>

			<ModalOverlay className={overlayClassName} />
		</div>
	);
}
